
import React from "react";
import { Program } from "@/services/db";
import { Badge } from "@/components/ui/badge";
import { Clock, DollarSign } from "lucide-react";
import { differenceInDays, format } from "date-fns";

interface ProgramItemProps {
  program: Program;
  index: number;
}

const ProgramItem = ({ program, index }: ProgramItemProps) => {
  const getFeeStatusColor = (status: string) => {
    switch(status) {
      case 'paid': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'pending': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'partial': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const calculateRealProgress = () => {
    const enrolledDate = new Date(program.enrolledAt);
    const completionDate = new Date(program.completionDate);
    const today = new Date();
    
    // Calculate total program duration in days
    const totalDays = differenceInDays(completionDate, enrolledDate);
    
    // Calculate days elapsed since enrollment
    const daysElapsed = differenceInDays(today, enrolledDate);
    
    // Calculate remaining days
    const remainingDays = differenceInDays(completionDate, today);
    
    // Calculate progress percentage (0-100)
    const progressPercentage = totalDays > 0 ? Math.min(Math.max((daysElapsed / totalDays) * 100, 0), 100) : 0;
    
    // Determine if program is completed
    const isCompleted = remainingDays <= 0;
    
    return {
      progressPercentage: Math.round(progressPercentage),
      remainingDays: Math.max(remainingDays, 0),
      isCompleted,
      totalDays,
      daysElapsed: Math.max(daysElapsed, 0)
    };
  };

  const progress = calculateRealProgress();

  return (
    <div className="bg-gray-50 dark:bg-nalc-dark/20 p-1.5 rounded-md border-l-2 border-nalc-teal/20">
      <div className="flex justify-between items-start mb-0.5">
        <span className="font-medium text-xs text-slate-800 dark:text-slate-200 truncate pr-1">
          {program.title}
        </span>
        <div className="flex items-center gap-0.5 text-xs">
          <DollarSign className="h-2.5 w-2.5 text-nalc-teal" />
          <span className="font-semibold text-nalc-teal">{program.fee}</span>
        </div>
      </div>
      
      <div className="text-xs text-muted-foreground space-y-0.5">
        <div className="flex items-center justify-between">
          <span className="text-xs truncate">{program.category}</span>
          <span className={`px-1 py-0.5 rounded-full text-xs ${getFeeStatusColor(program.feeStatus)}`}>
            {program.feeStatus.charAt(0).toUpperCase() + program.feeStatus.slice(1)}
          </span>
        </div>
        
        {/* Real Progress Display */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-0.5">
            <Clock className="h-2.5 w-2.5" />
            <span>
              {progress.isCompleted 
                ? "Completed" 
                : `${progress.remainingDays} days left`}
            </span>
          </div>
          <span className={`font-medium ${progress.isCompleted ? 'text-green-600' : 'text-blue-600'}`}>
            {progress.progressPercentage}% 
            {progress.isCompleted ? ' ✓' : ''}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
          <div 
            className={`h-1 rounded-full transition-all duration-300 ${
              progress.isCompleted ? 'bg-green-500' : 'bg-blue-500'
            }`} 
            style={{ width: `${progress.progressPercentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default ProgramItem;
